import { z } from 'zod';
import { decimalNoNegativo, rif, uuid } from './comun.ts';
import type { CuadreCostosDTO } from './costos.ts';

/**
 * Rango máximo de un reporte con detalle y de su Excel: un año (hallazgo T32 de la auditoría de 2000 días: el reporte de
 * IGTF de 5,5 años pesaba 10 MB y el Excel tomaba más de 600 MB de memoria). La declaración es por quincena.
 */
export const MAX_DIAS_REPORTE = 366;
const diasEntre = (desde: string, hasta: string) => Math.round((Date.parse(hasta) - Date.parse(desde)) / 86_400_000);
const hastaUnAnio = (r: { desde?: string | undefined; hasta?: string | undefined }, ctx: z.RefinementCtx) => {
  if (r.desde && r.hasta && diasEntre(r.desde, r.hasta) > MAX_DIAS_REPORTE) {
    ctx.addIssue({ code: 'custom', path: ['hasta'], message: `Elige hasta un año (${MAX_DIAS_REPORTE} días) entre «Desde» y «Hasta»` });
  }
};
const rangoBase = z.object({ desde: z.iso.date(), hasta: z.iso.date(), sucursal_id: uuid.optional() });
export const rangoQuery = rangoBase.superRefine(hastaUnAnio);
export type RangoQuery = z.infer<typeof rangoQuery>;
/**
 * Período de los libros: AAAAMM con el mes de 01 a 12 (re-auditoría 2026-10-18, ronda 2, hallazgo 6-H15). Antes solo se
 * pedían seis cifras y un mes 13 o 00 daba un error inesperado (o un libro de compras vacío como «Período cerrado»).
 */
export const PERIODO_AAAAMM = /^\d{4}(0[1-9]|1[0-2])$/;
const periodoLibro = z.string().regex(PERIODO_AAAAMM, 'Período AAAAMM: el mes va de 01 a 12 (por ejemplo, 202610)');
export const periodoLibroQuery = z.object({ periodo: periodoLibro, sucursal_id: uuid.optional() });
export const estimacionQuery = z.object({ dias: z.coerce.number().int().min(7).max(365).default(30), sucursal_id: uuid.optional() });
/** Reporte de inventario: además del rango, filtra mermas, despieces y mínimos por ubicación y por producto (código o nombre). */
export const reporteInventarioQuery = z.object({ sucursal_id: uuid.optional(), desde: z.iso.date().optional(), hasta: z.iso.date().optional(), almacen_id: uuid.optional(), producto: z.string().trim().max(80).optional() });
/** Cambios y devoluciones: por fechas y por cliente (nombre o RIF). */
export const reporteCambiosQuery = rangoBase.extend({ cliente: z.string().trim().max(80).optional() }).superRefine(hastaUnAnio);
/** Ajustes de inventario: por fechas (de registro), ubicación, estado y producto. */
export const reporteAjustesQuery = rangoBase.extend({ almacen_id: uuid.optional(), estado: z.enum(['pendiente', 'aprobado', 'rechazado']).optional(), producto: z.string().trim().max(80).optional() }).superRefine(hastaUnAnio);
/** Traslados: por fechas (de despacho), ubicación (origen o destino), producto y solo los que tuvieron diferencia. */
export const reporteTrasladosQuery = rangoBase.extend({ almacen_id: uuid.optional(), producto: z.string().trim().max(80).optional(), con_diferencia: z.enum(['true', 'false']).optional() }).superRefine(hastaUnAnio);
export const REPORTES_XLSX = ['ventas', 'inventario', 'compras', 'estimacion', 'cxc', 'cxp', 'cierres', 'igtf', 'cambios', 'ajustes', 'traslados', 'tiendas'] as const;
export const xlsxReporteQuery = z.object({ tipo: z.enum(REPORTES_XLSX), desde: z.iso.date().optional(), hasta: z.iso.date().optional(), sucursal_id: uuid.optional(), dias: z.coerce.number().int().min(7).max(365).optional(), almacen_id: uuid.optional(), producto: z.string().trim().max(80).optional(), cliente: z.string().trim().max(80).optional(), estado: z.enum(['pendiente', 'aprobado', 'rechazado']).optional(), con_diferencia: z.enum(['true', 'false']).optional() }).superRefine(hastaUnAnio);
/** `inventario` es el libro de inventario real y `inventario_fiscal` el fiscal (spec 13, enmienda 2026-10-29). */
export const xlsxLibroQuery = z.object({ libro: z.enum(['ventas', 'compras', 'inventario', 'inventario_fiscal']), periodo: periodoLibro, sucursal_id: uuid.optional() });
/** Libro de inventario (art. 177 del Reglamento de la Ley de ISLR): de toda la empresa, por mes. */
export const libroInventarioQuery = z.object({ periodo: periodoLibro });
/**
 * Estado de un traslado como lo lee la gente, en la pantalla y en el Excel de Reportes (re-auditoría 2026-10-18,
 * ronda 2, hallazgo 6-H18: el Excel sacaba el nombre interno).
 */
export const ESTADO_TRASLADO_ETIQUETA: Record<string, string> = { en_transito: 'En tránsito', recibido_parcial: 'Recibido en parte', recibido: 'Recibido', cancelado: 'Cancelado' };

export interface FilaAgrupada {
  clave: string;
  nombre: string;
  documentos: number;
  total: string;
}

/**
 * Tope de renglones del DETALLE de cada reporte. Los totales siempre son del rango completo; cuando el detalle llega al
 * tope la pantalla lo avisa y pide acotar las fechas.
 */
export const LIMITE_DETALLE_REPORTE = { ventas: 500, pendientes_entrega: 200, mermas: 300, despieces: 200, cxc: 500, cambios: 500, cxp: 500 } as const;
/** Movimientos que trae el kardex de un producto (los más recientes): con más, la suma ya no reconstruye la existencia. */
export const LIMITE_KARDEX = 200;

export interface ReporteVentasDTO {
  desde: string;
  hasta: string;
  sucursal_nombre: string | null;
  documentos: number;
  /** FAC + ND − NC (fiscal) y NOTA − CAM (no fiscal), en $. */
  total_fiscal: string;
  total_no_fiscal: string;
  total: string;
  por_tipo: FilaAgrupada[];
  por_caja: FilaAgrupada[];
  por_cajero: FilaAgrupada[];
  por_sucursal: FilaAgrupada[];
  /** Columnas por método de pago (cobros y abonos del rango) en $ equivalente. */
  por_metodo: { metodo: string; moneda: 'USD' | 'BS'; monto: string; monto_usd: string; operaciones: number }[];
  /** Σ columnas por método = total cobrado del rango (criterio 2). No suma crédito de cambio ni saldo a favor usado. */
  /** Lo cobrado en el rango, sin el vuelto (lo entregado de más y devuelto no es dinero cobrado). */
  total_cobrado: string;
  /** Vuelto entregado en el rango, en $: los métodos muestran lo recibido; esto es lo que se devolvió. */
  total_vueltos: string;
  /** Quien consulta tiene su caja abierta sin declarar: "por caja" y "por método" no incluyen esa caja (conteo a ciegas). */
  caja_propia_oculta?: boolean;
  documentos_detalle: { id: string; tipo: string; numero_formateado: string; emitido_en: string; cliente_razon_social: string; caja_nombre: string | null; cajero_nombre: string; total: string; estado_pago: string }[];
}

/**
 * Los importes a costo (valorizado, valor de mermas y despieces) van en null para quien no tiene el permiso
 * `productos.costos`: ve cantidades, no costos.
 */
export interface ReporteInventarioDTO {
  sucursal_nombre: string | null;
  /** Almacenes más lo que va en camino (a costo de la guía). */
  valorizado_total: string | null;
  /** Traslados despachados sin recibir: cuentan en la tienda de origen hasta que llegan (spec 07). */
  en_camino: { traslados: number; unidades: string; valorizado: string | null };
  por_almacen: { almacen_id: string; almacen_nombre: string; sucursal_nombre: string; productos: number; unidades: string; valorizado: string | null; pendiente_entrega: string }[];
  pendientes_entrega: { documento_id: string; numero_formateado: string; cliente_razon_social: string; emitido_en: string; dias: number; unidades_pendientes: string }[];
  /** Roturas, mermas, daños y pérdidas aprobados, un renglón por producto (los despieces van aparte). */
  mermas: { ajuste_id: string; numero_formateado: string; fecha: string; motivo: string; almacen_nombre: string; producto_codigo: string; producto_nombre: string; unidades: string; valor: string | null }[];
  mermas_total: string | null;
  /**
   * Despieces: caja rota → piezas sueltas (`merma_valor` = lo consumido menos lo recuperado, a costo), lámina de
   * vidrio → piezas con medida (`piezas_merma` = las que se descartaron; `merma_valor` = la lámina menos lo
   * aprovechado) y producto con detalle → unidades que pasaron a su lista (no es merma).
   */
  despieces: {
    id: string;
    origen: 'caja' | 'vidrio' | 'detalle';
    numero_formateado: string;
    fecha: string;
    almacen_nombre: string;
    motivo: string;
    producto_codigo: string;
    producto_nombre: string;
    cantidad: string;
    piezas: string;
    piezas_merma: number | null;
    costo_consumido: string | null;
    merma_valor: string | null;
  }[];
  /**
   * true solo si quedaron despieces fuera de la lista (se muestran los más recientes, hasta el tope): re-auditoría
   * 2026-10-18, ronda 2, hallazgo 6-H16 (antes se avisaba al llegar al tope aunque no faltara ninguno).
   */
  despieces_recortado: boolean;
  /** Productos bajo el mínimo total de la sucursal. */
  bajo_minimo: { producto_id: string; codigo: string; nombre: string; disponible: string; minimo: string }[];
  /** Ubicaciones (tienda o depósito) con existencia bajo su mínimo o cerca de él. */
  minimos_ubicacion: { producto_id: string; codigo: string; nombre: string; almacen_id: string; almacen_nombre: string; sucursal_nombre: string; existencia: string; /** Existencia menos lo vendido por entregar: contra esto se compara el mínimo. */ disponible: string; minimo: string; estado: 'bajo' | 'cerca' }[];
}

export interface ReporteComprasDTO {
  desde: string;
  hasta: string;
  /** Compras con factura del período (lo que va a los libros fiscales). */
  total_facturas: string;
  /** Compras con nota de entrega, sin factura: para el ISLR son gasto no deducible (contadora, 29-10). */
  total_sin_factura: string;
  total_retenido_iva: string;
  total_retenido_islr: string;
  /** `compras` y `total`: con factura; `notas` y `sin_factura`: con nota de entrega. */
  por_proveedor: { proveedor_id: string; rif: string; razon_social: string; compras: number; total: string; notas: number; sin_factura: string; retencion_iva: string; retencion_islr: string; cxp_saldo: string }[];
  /**
   * Costos por producto de las compras del período (spec 16): costo de factura, gastos con factura (servicios cargados
   * al costo), gastos sin factura (Gastos asociados) que viajan con esas compras, costo real y margen contra la lista
   * principal.
   */
  por_producto: { producto_id: string; codigo: string; nombre: string; unidades: string; costo_factura: string; costo_sin_factura: string; gastos_con_factura: string | null; gastos_sin_factura: string | null; costo_real: string | null; costo_unitario_promedio: string | null; precio_lista: string | null; margen_pct: string | null; proveedores: string }[];
  /**
   * Cuadre entre Compras, Gastos y el costo de los productos (caso i): debe cuadrar siempre. El costo real, los gastos,
   * el margen y el cuadre son confidenciales: `null` para quien no tiene `productos.costos` (el costo de factura es fiscal).
   */
  cuadre: CuadreCostosDTO | null;
}

export interface EstimacionCompraDTO {
  dias: number;
  desde: string;
  hasta: string;
  filas: { producto_id: string; codigo: string; nombre: string; unidad: string; demanda: string; demanda_diaria: string; disponible: string; minimo: string; cobertura_dias: string | null; sugerido: string; proveedor_habitual: string | null; ultimo_costo: string | null }[];
}

export interface ReporteCxcDTO {
  total: string;
  vencido: string;
  /** Lista de cobranza: por cliente, con su teléfono, lo vencido y la antigüedad de la deuda (días del documento más atrasado). */
  clientes: {
    cliente_id: string;
    rif: string;
    razon_social: string;
    telefono: string | null;
    documentos: number;
    total: string;
    vencido: string;
    mas_antiguo: string | null;
    dias_vencido: number;
    /** Saldo por tramos de atraso: sin vencer · 1 a 30 días · 31 a 60 · más de 60. */
    tramos: { al_dia: string; d1_30: string; d31_60: string; d61_mas: string };
    saldo_a_favor: string;
  }[];
  documentos: { id: string; numero_formateado: string; tipo: string; cliente_razon_social: string; emitido_en: string; vence_en: string | null; saldo_pendiente: string; dias_vencido: number }[];
}

export interface ReporteCxpDTO {
  total: string;
  proveedores: { proveedor_id: string; rif: string; razon_social: string; compras: number; saldo: string; mas_antigua: string | null }[];
  /** `tipo_documento`: la nota de entrega del proveedor (sin factura) también es deuda real. */
  compras: { id: string; numero_formateado: string | null; numero_factura: string; tipo_documento: 'factura' | 'nota_entrega'; proveedor_razon_social: string; fecha_factura: string; neto: string; cxp_saldo: string }[];
}

/**
 * IGTF percibido para la declaración: montos en la DIVISA recibida y su equivalente en Bs a la tasa BCV del día del
 * cobro. Incluye FAC/ND pagadas en divisas o con saldo a favor (cuenta como divisas: pedido de la dueña 2026-10-19).
 * El abono no lleva IGTF desde esa fecha; los anticipos anteriores, que lo percibieron al recibirse, siguen en el
 * reporte. Resumen por día con su quincena.
 */
export interface ReporteIgtfDTO {
  desde: string;
  hasta: string;
  sucursal_nombre: string | null;
  /** Cobros con pago en divisas más facturas manuales con IGTF (lo que suman las columnas «Operaciones»). */
  operaciones: number;
  /** Solo los cobros con pago en divisas, sin las facturas manuales (re-auditoría 2026-10-18, ronda 2, hallazgo 6-H1). */
  cobros: number;
  total_base_divisa: string;
  total_igtf_divisa: string;
  total_igtf_bs: string;
  /** IGTF en $ BCV (moneda de los documentos): lo que entró a caja por el impuesto. */
  total_igtf_usd: string;
  /** Lo pagado en divisas llevado a Bs con la tasa BCV de cada cobro (presentación: el IGTF no se recalcula con esto). */
  total_pagado_divisas_bs: string;
  /** Facturas y notas de débito del reporte, cada una contada UNA vez aunque tenga varios cobros en divisas. */
  facturas: { documentos: number; base_bs: string; exento_bs: string; iva_bs: string; total_bs: string };
  por_dia: { fecha: string; quincena: '1ra' | '2da'; operaciones: number; base_divisa: string; igtf_divisa: string; pagado_divisas_bs: string; igtf_bs: string }[];
  /** Subtotal por quincena de cada mes (AAAA-MM): el IGTF se declara por quincena. */
  por_quincena: { mes: string; quincena: '1ra' | '2da'; operaciones: number; base_divisa: string; igtf_divisa: string; pagado_divisas_bs: string; igtf_bs: string }[];
  /** IGTF escrito a mano en facturas de talonario manual (solo Bs): incluido en `total_igtf_bs`, `por_dia` y `por_quincena`. */
  total_igtf_manuales_bs: string;
  manuales: { id: string; fecha: string; numero: string; control: string | null; cliente_rif: string; cliente_razon_social: string; igtf_bs: string }[];
  pagos: {
    pago_id: string;
    recibido_en: string;
    /** Fecha del cobro en hora de Caracas (AAAA-MM-DD). */
    fecha: string;
    documento_id: string | null;
    /** FAC, ND o ABONO (ABONO: solo anticipos anteriores al 2026-10-19, que percibieron IGTF al recibirse). */
    documento_tipo: string;
    documento_numero: string | null;
    control: string | null;
    cliente_rif: string;
    cliente_razon_social: string;
    /** Instrumentos en divisa con su referencia (efectivo $, Zelle, USDT). */
    instrumentos: string | null;
    igtf_pct: string;
    base_divisa: string;
    igtf_divisa: string;
    igtf_bs: string;
    tasa_bcv_cobro: string | null;
    igtf_usd: string;
    /** Bs fiscales guardados en la factura o nota de débito (null en un anticipo: no tiene factura). */
    factura_base_bs: string | null;
    factura_exento_bs: string | null;
    factura_iva_bs: string | null;
    factura_total_bs: string | null;
    /** Base pagada en divisas × tasa BCV del cobro, en Bs. */
    pagado_divisas_bs: string | null;
  }[];
}

export interface ReporteCierresDTO {
  desde: string;
  hasta: string;
  sesiones: { sesion_id: string; caja_nombre: string; usuario_nombre: string; abierta_en: string; cerrada_en: string | null; estado: string; /** null: es la caja abierta de quien consulta (conteo a ciegas). */ cobrado_usd: string | null; egresos_usd: string; /** Retiros parciales de efectivo, en $ equivalente. */ retiros_usd: string; diferencia_usd: string | null; /** Suma de las diferencias de cada método, sin compensar faltantes con sobrantes. */ diferencia_abs_usd: string | null; reaperturas: number; justificacion: string | null }[];
  por_cajero: { usuario_nombre: string; cierres: number; diferencia_acumulada: string; con_diferencia: number }[];
  total_diferencias: string;
}

export interface FilaLibroVentasDTO {
  fecha: string;
  documento_id: string;
  /**
   * RET = retención recibida en este período de una factura de un período anterior (fila solo de retención).
   * IGTF = IGTF percibido en este período que no corresponde a una factura del período: el cobro en divisas de una
   * factura de un período anterior, o un anticipo (fila solo de IGTF; `numero` es la factura o el recibo del anticipo).
   */
  tipo: 'FAC' | 'NC' | 'ND' | 'RET' | 'IGTF';
  numero: string;
  control: string | null;
  rif: string;
  razon_social: string;
  tasa: string;
  /** Tasa con que el documento calculó sus Bs (FAC, NC y ND); en las demás filas no viene. */
  tasa_aplicada?: string | null;
  total_con_iva: string;
  exento: string;
  base: string;
  iva: string;
  /**
   * IGTF percibido en los cobros de la factura dentro del período, en Bs (a la BCV de cada cobro). Lo que se cobra en
   * otro período sale allí como fila IGTF: la columna suma lo percibido en el mes y cuadra con el reporte de IGTF.
   */
  igtf: string;
  /**
   * IVA retenido por el cliente, en Bs: entra en el libro del mes en que se RECIBIÓ el comprobante (FIS-11). Una
   * factura cuyo comprobante aún no llega va en cero; si llega en otro mes, sale allí como fila RET.
   */
  iva_retenido: string;
  comprobante_iva: string | null;
  /**
   * Fecha que trae el comprobante de retención de IVA. En una factura manual va vacía: el sistema no la guarda, solo el
   * día en que se recibió (re-auditoría 2026-10-18, ronda 2, hallazgo 6-H10).
   */
  comprobante_fecha: string | null;
  comprobante_version: number | null;
  retencion_municipal: string;
  comprobante_municipal: string | null;
  /** true si la retención fue corregida (regla 10): se marca en pantalla, limpio en el export. */
  corregida: boolean;
  documento_origen: string | null;
  /** Número y control anulados por hoja dañada: fila en cero con la leyenda ANULADA. */
  anulada: boolean;
  /** Factura de talonario manual registrada a mano para el libro. */
  manual: boolean;
  /** Solo en filas IGTF: el cobro que percibió el impuesto y si viene de un anticipo (sin documento de venta). */
  pago_id?: string | null;
  anticipo?: boolean;
}

export interface LibroVentasDTO {
  periodo: string;
  sucursal_nombre: string | null;
  preliminar: boolean;
  /** Por qué es PRELIMINAR (re-auditoría 2026-10-18, ronda 2, hallazgo 6-H13): el mes no ha terminado… */
  mes_en_curso: boolean;
  /** …o hay cajas del período todavía abiertas. */
  cajas_abiertas: number;
  reaperturas: number;
  filas: FilaLibroVentasDTO[];
  totales: { documentos: number; anuladas: number; total_con_iva: string; exento: string; base: string; iva: string; igtf: string; iva_retenido: string; retencion_municipal: string };
  /** Regla 11: Σ documentos del período (consulta independiente) vs Σ filas. */
  verificacion: { documentos_total: string; documentos_iva: string; cuadra: boolean; diferencia: string; cruces?: CruceLibroDTO[] };
}

/**
 * Cruce de un total del libro contra otra fuente del sistema que debe dar lo mismo (hallazgo T21 de la auditoría de 2000
 * días): el IGTF del libro de ventas contra el reporte de IGTF del mes; lo retenido del libro de compras contra los
 * comprobantes de IVA (los del TXT) y de ISLR (los del XML) del período. Solo lectura: no cambia ninguna cifra.
 */
export interface CruceLibroDTO {
  concepto: string;
  libro: string;
  contra: string;
  /** De dónde sale «contra» (p. ej. «Reporte de IGTF del mes»). */
  fuente: string;
  cuadra: boolean;
}

/**
 * Motivo real de PRELIMINAR del libro de ventas, el mismo en la pantalla y en el Excel (re-auditoría 2026-10-18,
 * ronda 2, hallazgo 6-H13): el mes en curso, las cajas abiertas del período o las dos cosas.
 */
export function motivoPreliminarVentas(l: Pick<LibroVentasDTO, 'mes_en_curso' | 'cajas_abiertas'>): string {
  return [l.mes_en_curso ? 'el mes no ha terminado' : null, l.cajas_abiertas > 0 ? `hay ${l.cajas_abiertas} caja(s) abierta(s) del período` : null].filter((x) => x !== null).join(' y ');
}

/**
 * `tipo` '01' = factura de compra del período. 'RET' = comprobante de retención numerado en este período de una compra
 * de OTRO período (fila solo de retención). La retención de una compra aparece en el libro del período en que se
 * numeró su comprobante: mientras sea provisional va en cero.
 */
export interface FilaLibroComprasDTO {
  /** Fecha de la factura del proveedor. */
  fecha: string;
  /** Fecha del período fiscal con la que entró al libro, si se indicó (si no, vale la de la factura). */
  fecha_fiscal?: string | null;
  compra_id: string;
  numero_interno: string | null;
  rif: string;
  razon_social: string;
  numero_factura: string;
  numero_control: string | null;
  tipo: string;
  tasa: string;
  total_con_iva: string;
  exento: string;
  base: string;
  iva: string;
  iva_retenido: string;
  comprobante_iva: string | null;
  comprobante_version: number | null;
  islr_retenido: string;
  corregida: boolean;
  /** La compra tiene comprobantes de retención todavía sin numerar: lo retenido no entra aún en ningún libro. */
  provisional: boolean;
}

export interface LibroComprasDTO {
  periodo: string;
  sucursal_nombre: string | null;
  /** PRELIMINAR mientras el mes no termine o haya compras en borrador del mes (las cajas abiertas no lo cambian). */
  preliminar: boolean;
  /** Compras en borrador del período: todavía pueden confirmarse y entrar al libro. */
  borradores: number;
  filas: FilaLibroComprasDTO[];
  totales: { compras: number; total_con_iva: string; exento: string; base: string; iva: string; iva_retenido: string; islr_retenido: string };
  verificacion: { compras_total: string; compras_iva: string; cuadra: boolean; diferencia: string; cruces?: CruceLibroDTO[] };
}

// ─── Libro de inventario (art. 177 RLISLR, spec 13 enmienda 2026-10-16) ─────
/**
 * Una fila por producto con movimiento o existencia en el mes. Unidades en la unidad base del producto; valores en Bs
 * a costo promedio ponderado del mes (las compras entran a su costo en Bs de la factura). Cada fila cuadra:
 * inicial + entradas − salidas − retiros − autoconsumos = final, en unidades y en valores.
 */
export interface FilaLibroInventarioDTO {
  producto_id: string;
  codigo: string;
  nombre: string;
  unidad: string;
  inicial: string;
  entradas: string;
  salidas: string;
  retiros: string;
  autoconsumos: string;
  final: string;
  valor_inicial: string;
  valor_entradas: string;
  valor_salidas: string;
  valor_retiros: string;
  valor_autoconsumos: string;
  valor_final: string;
  costo_unitario: string;
}

/**
 * De qué está hecha cada columna: valor en Bs de cada concepto en el mes y en cuántos productos (toda la empresa). Las
 * unidades no se suman entre productos: cada uno tiene la suya (metros, sacos, piezas).
 */
export interface ConceptoLibroInventarioDTO {
  columna: 'entradas' | 'salidas' | 'retiros' | 'autoconsumos';
  concepto: string;
  productos: number;
  valor: string;
}

export interface LibroInventarioDTO {
  periodo: string;
  preliminar: boolean;
  empresa: { razon_social: string; rif: string; direccion_fiscal: string };
  filas: FilaLibroInventarioDTO[];
  totales: { productos: number; valor_inicial: string; valor_entradas: string; valor_salidas: string; valor_retiros: string; valor_autoconsumos: string; valor_final: string };
  conceptos: ConceptoLibroInventarioDTO[];
  /**
   * Verificación independiente: la existencia final del libro contra la del kardex de cada almacén al cierre del mes
   * (más lo que va en camino entre almacenes y lo que está en «productos con detalles»). Si no cuadra es un bug.
   */
  verificacion: { cuadra: boolean; diferencias: { codigo: string; libro: string; kardex: string }[] };
}

// ─── Libro de inventario fiscal (spec 13, enmienda 2026-10-29) ─────────────────
/**
 * Lo que dice el pie del libro de inventario fiscal, en la pantalla y en el Excel: por qué no coincide con el real
 * (pregunta de la contadora D11 y respuestas del 29-10 a las propuestas de retiros y de compras con nota).
 */
export const NOTA_LIBRO_INVENTARIO_FISCAL =
  'El libro fiscal solo cuenta lo facturado: entran las compras con factura, las notas de crédito de facturas y el inventario inicial, y salen las facturas al emitirse. La diferencia con el libro real son las notas de entrega (de proveedores y de clientes), los Cambios, los ajustes, los despieces, las roturas y lo facturado que todavía no se entrega.';
/** Lo que dice el pie cuando hay existencias negativas. */
export const NOTA_EXISTENCIA_NEGATIVA_FISCAL =
  'Existencia negativa: se facturó más de lo que entró con factura (por ejemplo, mercancía que llegó con nota de entrega del proveedor o por un ajuste). Sale tal cual, marcada. Lo que sale sin existencia fiscal que lo respalde sale sin valor, y lo que entra mientras siga negativa queda en la fila hasta cubrirla.';

/** Fila del libro fiscal: la misma del real, con la marca de existencia negativa (inicial o final). */
export interface FilaLibroInventarioFiscalDTO extends FilaLibroInventarioDTO {
  negativa: boolean;
}

/** Producto cuya existencia final (en unidades o en Bs) no coincide entre el libro real y el fiscal. */
export interface DiferenciaInventarioRealFiscalDTO {
  producto_id: string;
  codigo: string;
  nombre: string;
  unidad: string;
  final_real: string;
  final_fiscal: string;
  /** final_real − final_fiscal, en unidades. */
  diferencia: string;
  valor_real: string;
  valor_fiscal: string;
}

/**
 * Libro de inventario FISCAL: mismas columnas, mismo período mensual y mismo método de valoración que el real, pero
 * solo con lo respaldado por documentos fiscales. Entradas: compras con factura, notas de crédito de facturas e
 * inventario inicial. Salidas: facturas al emitirse (no al entregarse). Retiros y autoconsumos siempre en cero.
 */
export interface LibroInventarioFiscalDTO {
  periodo: string;
  preliminar: boolean;
  empresa: { razon_social: string; rif: string; direccion_fiscal: string };
  filas: FilaLibroInventarioFiscalDTO[];
  totales: LibroInventarioDTO['totales'];
  conceptos: ConceptoLibroInventarioDTO[];
  /** Cuántas filas tienen existencia negativa. */
  negativas: number;
  /**
   * Verificación independiente: la existencia final de cada producto en el libro (encadenado mes a mes) contra la suma
   * directa de sus documentos fiscales hasta el cierre del mes. Si no cuadra es un error del sistema.
   */
  verificacion: { cuadra: boolean; diferencias: { codigo: string; libro: string; documentos: string }[] };
  /** Por producto, la existencia final del libro real y la del fiscal cuando no coinciden. */
  diferencias_real: DiferenciaInventarioRealFiscalDTO[];
}

// ─── Facturas manuales (talonarios serie A/B) para el libro de ventas ───────
export const facturaManualInput = z.object({
  /** Solo para roles globales sin contexto (contador): la sucursal del talonario. */
  sucursal_id: uuid.optional(),
  tipo: z.enum(['FAC', 'NC', 'ND']).default('FAC'),
  numero: z.coerce.number().int().min(1).max(99_999_999),
  control: z.string().trim().max(20).optional(),
  fecha: z.iso.date(),
  cliente_rif: rif,
  cliente_razon_social: z.string().trim().min(2).max(200),
  /** Importes de la factura, en bolívares, tal como se escribió a mano. */
  base_bs: decimalNoNegativo,
  exento_bs: decimalNoNegativo.default('0'),
  iva_bs: decimalNoNegativo,
  total_bs: decimalNoNegativo,
  igtf_bs: decimalNoNegativo.default('0'),
  retencion_iva_bs: decimalNoNegativo.default('0'),
  comprobante_retencion: z.string().trim().max(20).optional(),
  /** Día en que se recibió el comprobante de retención (por defecto, el de la factura): su mes es el del libro. */
  retencion_recibida_el: z.iso.date().optional(),
  /** Fecha impresa en el comprobante de retención (columna «Fecha comprobante» del libro; hallazgo T46). */
  comprobante_fecha: z.iso.date().optional(),
  observaciones: z.string().trim().max(300).optional(),
});
export type FacturaManualInput = z.infer<typeof facturaManualInput>;
export type FacturaManualEntrada = z.input<typeof facturaManualInput>;
export const facturaManualAnularInput = z.object({ motivo: z.string().trim().min(5, 'Indica el motivo').max(300) });
/**
 * Retención de IVA que llega después de registrar la factura manual sin ella (hallazgo T47): se carga una sola vez.
 */
export const facturaManualRetencionInput = z.object({
  retencion_iva_bs: decimalNoNegativo,
  comprobante_retencion: z.string().trim().min(1, 'Escribe el número del comprobante').max(20),
  comprobante_fecha: z.iso.date().optional(),
  retencion_recibida_el: z.iso.date(),
});
export type FacturaManualRetencionInput = z.infer<typeof facturaManualRetencionInput>;
export const facturasManualesFiltros = z.object({ desde: z.iso.date().optional(), hasta: z.iso.date().optional(), sucursal_id: uuid.optional() });

export interface FacturaManualDTO {
  id: string;
  sucursal_nombre: string;
  tipo: 'FAC' | 'NC' | 'ND';
  numero_formateado: string;
  control: string | null;
  fecha: string;
  cliente_rif: string;
  cliente_razon_social: string;
  base_bs: string;
  exento_bs: string;
  iva_bs: string;
  total_bs: string;
  igtf_bs: string;
  retencion_iva_bs: string;
  comprobante_retencion: string | null;
  /** Día en que se recibió el comprobante de retención; null = el de la factura (las registradas antes del 2026-10-18). */
  retencion_recibida_el: string | null;
  /** Fecha impresa en el comprobante (vacía en las de antes del 2026-10-24). */
  comprobante_fecha: string | null;
  anulada: boolean;
  motivo_anulacion: string | null;
  observaciones: string | null;
  registrado_por_nombre: string;
  creado_en: string;
}

/** Cambios de material y devoluciones: qué se recibió, qué se despachó a cambio y qué pasó con el crédito. */
export interface ReporteCambiosDTO {
  desde: string;
  hasta: string;
  documentos: number;
  total_credito: string;
  filas: {
    id: string;
    fecha: string;
    tipo: 'NC' | 'CAM';
    numero_formateado: string;
    origen_tipo: string | null;
    origen_numero: string | null;
    cliente_rif: string;
    cliente_razon_social: string;
    sucursal_nombre: string;
    cajero_nombre: string;
    motivo: string | null;
    recibido: { producto_codigo: string; producto_nombre: string; unidades: string; danada: boolean }[];
    despachado: { producto_codigo: string; producto_nombre: string; unidades: string }[];
    despacho_tipo: string | null;
    despacho_numero: string | null;
    despacho_total: string | null;
    credito: string;
    /** Parte del crédito usada en lo que se llevó. */
    aplicado: string;
    /** Lo que el cliente pagó de diferencia. */
    diferencia_cobrada: string;
    /** Lo que quedó a su favor (saldo) o se le devuelve por transferencia. */
    saldo_generado: string;
    reintegro_usd: string | null;
    reintegro_estado: 'pendiente' | 'pagado' | null;
  }[];
}

// ─── Bloque 12 (2026-10-10): ajustes, traslados y ventas por tienda ─────────
/**
 * Ajustes de inventario del período: un renglón por producto ajustado, con quién lo propuso y quién lo resolvió.
 * Los importes a costo van en null para quien no tiene el permiso `productos.costos`.
 */
export interface ReporteAjustesDTO {
  desde: string;
  hasta: string;
  sucursal_nombre: string | null;
  ajustes: number;
  pendientes: number;
  aprobados: number;
  rechazados: number;
  /** Valor a costo de lo que entró y de lo que salió en los ajustes APROBADOS. */
  valor_entradas: string | null;
  valor_salidas: string | null;
  /** Aprobados agrupados por motivo. */
  por_motivo: { motivo: string; ajustes: number; unidades_entrada: string; unidades_salida: string; valor: string | null }[];
  /** true = el detalle se recortó; los totales sí son del período completo. */
  truncado: boolean;
  filas: {
    ajuste_id: string;
    numero_formateado: string;
    fecha: string;
    tipo: string;
    motivo: string;
    estado: 'pendiente' | 'aprobado' | 'rechazado';
    sucursal_nombre: string;
    almacen_nombre: string;
    creado_por_nombre: string;
    resuelto_por_nombre: string | null;
    resuelto_en: string | null;
    justificacion: string;
    producto_codigo: string;
    producto_nombre: string;
    /** Con signo: negativo = salida. */
    cantidad: string;
    valor: string | null;
  }[];
}

/** Traslados del período: un renglón por producto de cada guía, con lo enviado, lo recibido y qué pasó con lo que faltó. */
export interface ReporteTrasladosDTO {
  desde: string;
  hasta: string;
  sucursal_nombre: string | null;
  guias: number;
  en_transito: number;
  con_diferencia: number;
  por_revisar: number;
  unidades_enviadas: string;
  unidades_recibidas: string;
  unidades_faltantes: string;
  por_ruta: { origen: string; destino: string; guias: number; enviadas: string; recibidas: string; faltantes: string }[];
  /** true = el detalle se recortó; los totales sí son del período completo. */
  truncado: boolean;
  filas: {
    traslado_id: string;
    numero_formateado: string;
    despachado_en: string;
    estado: string;
    origen: string;
    destino: string;
    vehiculo: string | null;
    chofer_nombre: string | null;
    producto_codigo: string;
    producto_nombre: string;
    enviado: string;
    recibido: string;
    /** Lo que no llegó de una guía ya cerrada (0 mientras sigue en tránsito). */
    faltante: string;
    faltante_causa: 'origen' | 'merma' | null;
    /** null = la guía no tiene diferencia. */
    diferencia_revisada: boolean | null;
  }[];
}

/** Consolidado: ventas de cada tienda en el período y los productos más vendidos (por unidades). */
export interface ReporteTiendasDTO {
  desde: string;
  hasta: string;
  tiendas: {
    sucursal_id: string;
    codigo: string;
    nombre: string;
    documentos: number;
    /** FAC + ND − NC y NOTA − CAM, en $. */
    ventas_fiscal: string;
    ventas_no_fiscal: string;
    ventas: string;
    ventas_bs: string;
    /** Cobros y abonos recibidos en el período, en $ equivalente. */
    cobrado: string;
    mas_vendido: { codigo: string; nombre: string; unidades: string; ventas: string } | null;
  }[];
  total: { documentos: number; ventas_fiscal: string; ventas_no_fiscal: string; ventas: string; ventas_bs: string; cobrado: string };
  mas_vendidos: { producto_id: string; codigo: string; nombre: string; unidades: string; ventas: string; por_tienda: { codigo: string; unidades: string }[] }[];
}
